import React, { useEffect, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Wallet, Banknote, TrendingUp, Loader2, ArrowLeft, ExternalLink,
  DollarSign, Music2, Sparkles, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { detectUserCurrency, loadFxRates, formatLocalPreviewFromUsd } from "@/lib/currency";
import WalletTipJarPoster from "@/components/WalletTipJarPoster";

const API = `${BACKEND_URL}/api`;

/**
 * DJ Wallet — one-glance earnings dashboard.
 * Reads from GET /api/me/wallet (this_week, this_month, lifetime, recent, stripe_balance).
 * Values shown in USD (tips + merch currency) with a preview of the DJ's own
 * local currency so they can eyeball what it's actually worth to them.
 * PRO subscription revenue not counted here since that belongs to Katwire
 * Labs, not the DJ.
 */
function fmt(cents) {
  const dollars = (cents || 0) / 100;
  return `$${dollars.toFixed(2)}`;
}
function fmtNoZero(cents) {
  return cents ? fmt(cents) : "—";
}
function timeAgo(iso) {
  if (!iso) return "";
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

const KIND_META = {
  live_tip:   { label: "Live tip",    icon: DollarSign, color: "#FFC107" },
  public_tip: { label: "Tip Jar",     icon: Music2,     color: "#00F0FF" },
  live_merch: { label: "Merch drop",  icon: Sparkles,   color: "#10B981" },
};

export default function WalletPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rates, setRates] = useState(null);
  const [localCurrency, setLocalCurrency] = useState("USD");

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/me/wallet`);
        if (!cancel) setData(r.data);
      } catch (err) {
        if (!cancel) setError(err.response?.data?.detail || "Could not load wallet");
      } finally { if (!cancel) setLoading(false); }
    })();
    setLocalCurrency(detectUserCurrency());
    loadFxRates().then(r => { if (!cancel && r) setRates(r); });
    return () => { cancel = true; };
  }, []);

  // Local-currency helper for a USD-cents value. Returns null if the user's
  // local currency already IS USD (no point showing "≈ $3.65 USD" twice).
  const localFor = (usdCents) => {
    if (!rates || localCurrency === "USD") return null;
    return formatLocalPreviewFromUsd((usdCents || 0) / 100, rates, localCurrency);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] text-white flex items-center justify-center" data-testid="wallet-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#10B981]"/>
      </div>
    );
  }

  const connected = data?.connected;
  const balance = data?.stripe_balance;

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white" data-testid="wallet-page">
      <header className="h-16 border-b border-[#1E2638] px-6 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#10B981] to-[#00F0FF] flex items-center justify-center">
            <Wallet className="w-5 h-5 text-black" strokeWidth={2.5}/>
          </div>
          <div className="font-display font-black text-xl tracking-tight">NEURAL<span className="text-[#10B981]">DECK</span> · WALLET</div>
        </Link>
        <Link to="/studio" className="font-mono text-xs uppercase tracking-widest text-[#94A3B8] hover:text-white">← Studio</Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#10B981] mb-2 flex items-center gap-2">
            <Banknote className="w-3 h-3"/> // your earnings
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl">Hey {user?.display_name?.split(" ")[0] || "DJ"} 👋</h1>
          <p className="text-[#94A3B8] mt-2">All amounts in <span className="text-[#00F0FF] font-black">USD</span> — tips + merch flow to your Stripe account, 90 % to you, 10 % to NeuralDeck.{localCurrency !== "USD" && rates && (
            <span className="block text-[11px] font-mono mt-1 text-[#94A3B8]/80" data-testid="wallet-fx-note">
              Local preview in <span className="text-[#10B981]">{localCurrency}</span> shown beside each amount · rate refreshed every 12h
            </span>
          )}</p>
        </div>

        {error && (
          <div className="rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/5 p-4 text-sm text-[#F8FAFC]" data-testid="wallet-error">
            <AlertTriangle className="w-4 h-4 inline mr-2 text-[#EF4444]"/>{error}
          </div>
        )}

        {!connected && (
          <div className="rounded-2xl border border-[#FFC107]/40 bg-[#FFC107]/5 p-6" data-testid="wallet-not-connected">
            <div className="font-display font-black text-xl">Connect Stripe to start receiving payouts</div>
            <div className="text-sm text-[#94A3B8] mt-1">You can already see all-time totals below, but until your Stripe account is connected, tips flow to a holding balance instead of your bank.</div>
            <Link to="/settings" className="mt-3 inline-block">
              <Button data-testid="wallet-connect-cta" className="bg-[#10B981] text-black hover:bg-[#34D399] font-black uppercase tracking-widest text-xs">
                Go to Settings → Payouts
              </Button>
            </Link>
          </div>
        )}

        {/* Balance + Payout */}
        {balance && (
          <div className="grid sm:grid-cols-2 gap-4" data-testid="wallet-balance-row">
            <StatCard title="Available now"
              label="Ready to payout to your bank"
              value={fmt(balance.available_cents)}
              icon={Banknote} color="#10B981"
              extra={data.payout_hint}/>
            <StatCard title="Pending"
              label="Clearing (Stripe holds for T+2)"
              value={fmt(balance.pending_cents)}
              icon={Loader2} color="#FFC107"/>
          </div>
        )}

        {/* Tip-jar QR poster — visible even before Stripe is connected so DJs
            can preview the flow; the destination /tip page shows the KYC
            gate when Connect isn't ready. */}
        <WalletTipJarPoster user={user}/>

        {/* Time buckets */}
        <div className="grid sm:grid-cols-3 gap-4" data-testid="wallet-time-row">
          <StatCard title="This week"
            label={`${data?.this_week?.count || 0} txn · net after 10 %`}
            value={fmt(data?.this_week?.net_cents)}
            icon={TrendingUp} color="#00F0FF"
            extra={`Gross ${fmt(data?.this_week?.gross_cents)}`}/>
          <StatCard title="This month"
            label={`${data?.this_month?.count || 0} txn · net`}
            value={fmt(data?.this_month?.net_cents)}
            icon={TrendingUp} color="#FF2A6D"
            extra={`Gross ${fmt(data?.this_month?.gross_cents)}`}/>
          <StatCard title="Lifetime"
            label={`${data?.lifetime?.count || 0} txn total`}
            value={fmt(data?.lifetime?.net_cents)}
            icon={Sparkles} color="#8B5CF6"
            extra={`Gross ${fmt(data?.lifetime?.gross_cents)}`}/>
        </div>

        {/* Recent transactions */}
        <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-6" data-testid="wallet-recent">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">// last 10</div>
              <div className="font-display font-black text-2xl">Recent earnings</div>
            </div>
            {connected && (
              <a href="https://dashboard.stripe.com/payments" target="_blank" rel="noopener noreferrer"
                data-testid="wallet-stripe-dashboard-link"
                className="text-xs font-mono uppercase tracking-widest text-[#00F0FF] hover:underline flex items-center gap-1">
                Full history <ExternalLink className="w-3 h-3"/>
              </a>
            )}
          </div>
          {(data?.recent?.length || 0) === 0 ? (
            <div className="text-center py-8 text-sm text-[#94A3B8]" data-testid="wallet-recent-empty">
              No earnings yet. Go live, drop merch, or share your <Link to="/settings" className="text-[#00F0FF] font-bold">tip-jar QR</Link>.
            </div>
          ) : (
            <div className="space-y-2">
              {data.recent.map((t, i) => {
                const meta = KIND_META[t.kind] || KIND_META.public_tip;
                const Icon = meta.icon;
                return (
                  <div key={i} data-testid={`wallet-recent-row-${i}`}
                    className="flex items-center gap-3 p-3 rounded-lg bg-[#0B0E14] border border-[#1E2638]">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}55` }}>
                      <Icon className="w-4 h-4" style={{ color: meta.color }}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-black text-sm" style={{ color: meta.color }}>{meta.label}</span>
                        <span className="text-xs text-[#94A3B8]">from {t.tipper_name || "anonymous"}</span>
                      </div>
                      {t.message && <div className="text-xs text-[#94A3B8] italic truncate mt-0.5">"{t.message}"</div>}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-display font-black text-lg text-white">{fmt(t.amount_cents)}</div>
                      <div className="text-[10px] font-mono text-[#94A3B8]">
                        -{fmt(t.platform_fee_cents)} fee · {timeAgo(t.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function StatCard({ title, label, value, extra, icon: Icon, color }) {
  return (
    <div className="rounded-xl border border-[#1E2638] bg-[#121620] p-5 flex items-start gap-4">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}15`, border: `1px solid ${color}55` }}>
        <Icon className="w-5 h-5" style={{ color }}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">{title}</div>
        <div className="font-display font-black text-2xl text-white mt-0.5">{value}</div>
        {label && <div className="text-[11px] text-[#94A3B8] mt-1">{label}</div>}
        {extra && <div className="text-[10px] font-mono text-[#94A3B8]/80 mt-0.5">{extra}</div>}
      </div>
    </div>
  );
}
